<template>
  <LineChart
    :options="chartOptions"
    :data="calculatedData"
    :plugins="plugins"
  />
</template>

<script setup lang="ts">
import { Chart as ChartJS, registerables } from "chart.js"
import { Line as LineChart } from "vue-chartjs"
import type {
  ChartData,
  Plugin,
  ChartOptions,
} from "chart.js"
import "chartjs-adapter-date-fns"

export type HoveredDataPoint = {
  index: number | undefined
  datasets: {
    label: string
    value: number
    color?: string | undefined
  }[]
  formattedLabel: string
}
const props = withDefaults(
  defineProps<{
    id: string
    datasets: {
      label: string
      data: number[]
      color?: string | undefined
    }[]
    labels: Label[]
    granularity?: Granularity | undefined
    isCumulative: boolean
    currency: string
    isolatedDatasetIndex?: number | null
    hoveredDatasetIndex?: number | null
    showTooltip?: boolean
  }>(),
  {
    granularity: undefined,
    isolatedDatasetIndex: null,
    hoveredDatasetIndex: null,
    showTooltip: false,
  }
)

const { formatToCurrencyString } = useCurrency()

ChartJS.register(...registerables)

type Label = {
  start: Date
  end: Date
}

const emit = defineEmits<{
  (
    event: "hover-datapoint",
    datapoint: HoveredDataPoint
  ): void
}>()

const minStart = computed(() =>
  DateFns.min(props.labels.map(({ start }) => start))
)
const maxEnd = computed(() =>
  DateFns.max(props.labels.map(({ end }) => end))
)
const buildCumulativeData = (data: number[]) => {
  let mapReduced: number[] = [0]
  let cumulativeSum = 0
  for (const value of data) {
    cumulativeSum = cumulativeSum + value
    mapReduced = [...mapReduced, cumulativeSum]
  }
  return mapReduced
}

const calculatedLabels = computed(() => {
  return props.isCumulative && props.labels.length >= 0
    ? [
        ...props.labels.map(({ start }) => start),
        maxEnd.value,
      ]
    : props.labels.map(({ start }) => start)
})

const colorPrimary = useCssVar("--color-primary")

const dimColor = (
  color: string | undefined
): string | undefined =>
  color?.replace(/^rgb\((.+)\)$/, "rgb($1 / 0.15)")

const calculatedDatasets = computed(() =>
  props.datasets.map((dataset, index) => {
    const isInteractive =
      props.hoveredDatasetIndex !== null ||
      props.isolatedDatasetIndex !== null
    const isFocused =
      index === props.hoveredDatasetIndex ||
      index === props.isolatedDatasetIndex

    return {
      label: dataset.label,
      data: props.isCumulative
        ? buildCumulativeData(dataset.data)
        : dataset.data,
      pointRadius: 0,
      pointHoverRadius: props.showTooltip ? 4 : 0,
      pointHoverBorderWidth: 0,
      pointHoverBackgroundColor: props.showTooltip
        ? dataset.color
        : undefined,
      borderColor:
        props.hoveredDatasetIndex !== null &&
        props.hoveredDatasetIndex !== index
          ? dimColor(dataset.color)
          : dataset.color,
      borderWidth: isInteractive
        ? isFocused
          ? 3
          : 1.5
        : undefined,
      hidden:
        props.isolatedDatasetIndex !== null &&
        props.isolatedDatasetIndex !== index,
    }
  })
)

const cumulativeTotals = computed(() =>
  props.datasets.map((dataset) =>
    dataset.data.reduce(
      (accumulator, value) => accumulator + value,
      0
    )
  )
)

const calculatedData = computed<
  ChartData<"line", number[], Date>
>(() => ({
  labels: calculatedLabels.value,
  datasets: calculatedDatasets.value,
}))

const colorText = useCssVar("--color-on-background")

const hoveredDatapointForWholeRange =
  computed<HoveredDataPoint>(() => ({
    index: undefined,
    datasets: props.datasets.map((dataset, index) => ({
      label: "",
      value: cumulativeTotals.value[index],
      color: dataset.color,
    })),
    formattedLabel: buildFormattedLabel({
      start: minStart.value,
      end: maxEnd.value,
    }),
  }))
const plugins: Plugin<"line">[] = [
  {
    id: "crosshair",
    afterDatasetsDraw: (
      chart_: ChartJS<"line", number[], Date>
    ) => {
      const activeElements =
        chart_?.tooltip?.getActiveElements()
      if (activeElements && activeElements.length > 0) {
        const activePoint = activeElements[0]
        const context = chart_.ctx
        const bottomY = chart_.chartArea.bottom
        const x = activePoint.element.x
        const y = activePoint.element.y
        context.save()
        context.beginPath()
        context.moveTo(x, y)
        context.lineTo(x, bottomY)
        context.lineWidth = 2
        context.strokeStyle = `rgb(${colorPrimary.value} / 0.3)`
        context.stroke()
        context.restore()
      }
    },
  },
  {
    id: "hoverEmitIndex",
    afterDatasetDraw: (chart_) => {
      const activeElements =
        chart_?.tooltip?.getActiveElements()

      const activePoint =
        activeElements && activeElements.length > 0
          ? activeElements[0]
          : undefined
      const index_ = activePoint
        ? props.isCumulative
          ? activePoint.index >= 1
            ? activePoint.index - 1
            : undefined
          : activePoint.index
        : undefined

      const hoveredDatapoint =
        index_ !== undefined
          ? {
              index: index_,
              datasets: props.datasets.map((dataset) => ({
                label: dataset.label,
                value: dataset.data[index_],
                color: dataset.color,
              })),
              formattedLabel: buildFormattedLabel(
                props.labels[index_],
                props.granularity
              ),
            }
          : hoveredDatapointForWholeRange.value
      emit("hover-datapoint", hoveredDatapoint)
    },
    afterEvent: (chart, { event }) => {
      if (event.native?.type === "touchend") {
        chart.tooltip?.setActiveElements([], { x: 0, y: 0 })
        chart.setActiveElements([])
        chart.update()
      }
    },
  },
]

const chartOptions: ChartOptions<"line"> = reactiveComputed(
  () => ({
    maintainAspectRatio: false,
    animation: false,
    responsive: true,
    plugins: {
      tooltip: props.showTooltip
        ? {
            enabled: true,
            mode: "index" as const,
            intersect: false,
            filter: (item) => !item.dataset.hidden,
            padding: 12,
            cornerRadius: 8,
            usePointStyle: true,
            boxWidth: 8,
            boxHeight: 8,
            callbacks: {
              title: (items) =>
                items[0]
                  ? formatToDate(
                      new Date(items[0].parsed.x)
                    )
                  : "",
              label: (context) =>
                ` ${context.dataset.label}: ${formatToCurrencyString(
                  context.parsed.y,
                  props.currency,
                  {
                    compact: false,
                    stripTrailingZeros: true,
                  }
                )}`,
            },
          }
        : {
            enabled: false,
            mode: "nearest" as const,
            intersect: false,
            axis: "x",
          },
      legend: {
        display: false,
      },
    },
    events: [
      "mousemove",
      "mouseout",
      "click",
      "touchstart",
      "touchmove",
      "touchend",
    ],
    // onHover: (...params) => emit("hover-datapoint", params),
    interaction: {
      mode: "index",
      intersect: false,
      axis: "x",
    },
    scales: {
      x: {
        stacked: false,
        type: "timeseries",
        time: {
          displayFormats: {
            month: "MMM",
          },
          unit: props.granularity,
          isoWeekday: true,
        },
        ticks: {
          font: {
            size: 12,
          },
          textStrokeColor: `rgb(${colorText.value} / 0.7)`,
          minRotation: 0,
          maxRotation: 0,
          padding: 8,
          align: "start",
          autoSkipPadding: 8,
          includeBounds: false,
        },
        grid: {
          color: `rgb(${colorText.value} / 0.05)`,
          drawTicks: false,
        },
      },
      y: {
        suggestedMax: 10000,
        title: {
          display: false,
        },
        grid: {
          display: false,
          drawTicks: false,
        },
        ticks: {
          align: "center",
          // align: "end",
          mirror: true,
          font: {
            size: 12,
          },
          callback: (value) =>
            value !== 0
              ? formatToCurrencyString(
                  Number(value),
                  props.currency,
                  {
                    compact: true,
                    stripTrailingZeros: true,
                  }
                )
              : "",
        },
        stacked: false,
        beginAtZero: true,
      },
    },
  })
)

const buildFormattedLabel = (
  { start, end }: { start: Date; end: Date },
  granularity?:
    | "hour"
    | "day"
    | "week"
    | "month"
    | "range"
    | "overview"
    | undefined
) => {
  return granularity === "hour"
    ? `${formatToTime(start)} - ${formatToTime(end)}`
    : granularity === "day"
      ? formatToDate(start)
      : `${formatToDate(start)} - ${formatToDate(end)}`
}
</script>
